import React, { useState } from 'react'
import PropTypes from 'prop-types'
import SelectCustomField from '../popups/select_custom_field'
import { useEffect } from 'react'

const internationalize = (...args) => !window.I18n ? '' : window.I18n.t(...args)

function MultipleCriteriaTable({
  criteria,
  golferLabel,
  teeAndHole,
  adjustmentHandicap,
  genderAdjustmentHandicap,
  dateForAge,
  useFirstRoundHI,
  divisions,
  customFields,
  customFieldAnswers,
  modalOpened,
  closeModal,
  setData,
  updateDivisionRow,
  getMatchingMembers,
  leagueMembersCount,
}) {

  const withHandicapRange = criteria.selected.includes('by_handicap')
  const withGenders = criteria.selected.includes('by_gender')
  const withAgeRange = criteria.selected.includes('by_age')
  const withCustomField = criteria.selected.includes('by_custom_field')
  const playingHandicap = criteria.selected_handicap === 'by_playing_handicap'

  const headers = [ {
    name: 'name',
    label: internationalize('divisions.index.by_multiple_criteria.division_name'),
    enabled: true,
  }, {
    name: 'total',
    label: internationalize('divisions.index.by_multiple_criteria.total'),
    enabled: true,
  }, {
    name: 'handicap',
    min_label: internationalize(`divisions.index.by_multiple_criteria.min_${playingHandicap ? 'ph' : 'hi'}`),
    max_label: internationalize(`divisions.index.by_multiple_criteria.max_${playingHandicap ? 'ph' : 'hi'}`),
    enabled: withHandicapRange,
  }, {
    name: 'genders',
    label: internationalize('divisions.index.by_multiple_criteria.genders'),
    enabled: withGenders,
  }, {
    name: 'age',
    min_label: internationalize('divisions.index.by_multiple_criteria.min_age'),
    max_label: internationalize('divisions.index.by_multiple_criteria.max_age'),
    enabled: withAgeRange,
  }, {
    name: 'custom_field',
    label: internationalize('divisions.index.by_multiple_criteria.custom_field'),
    enabled: withCustomField,
  }, {
    name: 'delete',
    label: '',
    enabled: true,
  } ]
  const enabledHeaders = headers.filter(x => x.enabled)
  const genders = [ 'All', 'M', 'F' ]

  const [ selectedDivisionId, setSelectedDivisionId ] = useState(null)
  const [ selectedGolfers, setSelectedGolfers ] = useState(0)
  const [ golfersLoading, setGolfersLoading ] = useState(false)
  const initialDivision = {
    name: null,
    numberOfGolfers: 0,
    minHandicap: null,
    maxHandicap: null,
    selectedGender: 'All',
    minAge: null,
    maxAge: null,
    metadatumId: null,
    metadatumAnswer: null,
  }

  const addRow = () => {
    const id = Date.now()
    setData([ 'create_division', 'multiple_criteria_divisions' ], {
      ...divisions,
      [id]: initialDivision,
    })
    fetchAndUpdateDivision(id, initialDivision)
  }

  const removeRow = (id) => {
    if (Object.keys(divisions).length > 1) {
      const updatedDivisions = { ...divisions }
      delete updatedDivisions[id]
      setData([ 'create_division', 'multiple_criteria_divisions' ], updatedDivisions)
      setSelectedGolfers(getSelectedGolfersCount(updatedDivisions))
    } else {
      delete divisions[id]
      setData([ 'create_division', 'multiple_criteria_divisions' ], {
        [id]: { ...initialDivision },
      })
      fetchAndUpdateDivision(id, initialDivision)
    }
  }

  const getSelectedGolfersCount = (divisionsObj) => {
    const allMemberIds = Object.values(divisionsObj).flatMap(d => d.memberIds || [])
    const uniqueIds = new Set(allMemberIds)
    return uniqueIds.size
  }

  const openModal = (id) => {
    setSelectedDivisionId(id)
    setData([ 'modal_opened' ], true)
  }

  const getCustomFieldLabel = (id) => {
    return customFields.choices.find(choice => choice.value === id)?.label || ''
  }

  const getMemberIds = async (division) => {
    let memberIds = []
    const hasCustomField = !withCustomField || (division.metadatumId && division.metadatumAnswer)
    const hasCourseAndTee = !playingHandicap || teeAndHole

    if (hasCustomField && hasCourseAndTee) {
      const minHandicap = withHandicapRange ? division.minHandicap || -100 : null
      const maxHandicap = withHandicapRange ? division.maxHandicap || 100 : null
      const selectedGender = withGenders ? division.selectedGender : null
      const minAge = withAgeRange ? division.minAge || 0 : null
      const maxAge = withAgeRange ? division.maxAge || 100 : null

      let fields = {
        min_hi: minHandicap,
        max_hi: maxHandicap,
        genders: selectedGender,
        min_age: minAge,
        max_age: maxAge,
        anchor_date: dateForAge,
        criteria_ph: playingHandicap,
        use_first_round_hi: useFirstRoundHI,
      }

      if (withCustomField) {
        fields = {
          ...fields,
          metadatum_id: division.metadatumId,
          answer: division.metadatumAnswer,
        }
      }

      if (playingHandicap) {
        fields = {
          ...fields,
          tee_id: teeAndHole?.virtual_id,
          hole_type: teeAndHole?.hole,
          adjustment_handicap: adjustmentHandicap,
          adjustment_handicap_M: genderAdjustmentHandicap?.['M'],
          adjustment_handicap_F: genderAdjustmentHandicap?.['F'],
        }
      }

      memberIds = await getMatchingMembers(fields)
    }
    return memberIds
  }

  const fetchAndUpdateDivision = async (id, updatedFields) => {
    const division = { ...divisions[id], ...updatedFields }
    const memberIds = await getMemberIds(division)
    const numberOfGolfers = memberIds.length
    const updatedDivision = { ...division, numberOfGolfers, memberIds }
    updateDivisionRow(id, updatedDivision)

    setSelectedGolfers(getSelectedGolfersCount({ ...divisions, [id]: updatedDivision }))
  }

  if (Object.keys(divisions).length === 0) {
    addRow()
  }

  useEffect(() => {
    const updateAllDivisions = async () => {
      const newDivisions = {}
      setGolfersLoading(true)

      for (const [ id, division ] of Object.entries(divisions)) {
        const memberIds = await getMemberIds(division)
        const numberOfGolfers = memberIds.length
        newDivisions[id] = { ...division, numberOfGolfers, memberIds }
      }
      setData([ 'create_division', 'multiple_criteria_divisions' ], newDivisions)
      setSelectedGolfers(getSelectedGolfersCount(newDivisions))
      setGolfersLoading(false)
    }
  
    if (Object.keys(divisions).length > 0) {
      updateAllDivisions()
    }
  }, [
    criteria,
    teeAndHole,
    adjustmentHandicap,
    genderAdjustmentHandicap,
    dateForAge,
    useFirstRoundHI,
  ])

  return (
    <div>
      <div className='table-responsive btsp_table_container'>
        <table className='divisions_by_multiple_criteria_table table with_striping tables_v2 table-striped btsp_table'>
          <thead>
            <tr>
              {
                enabledHeaders.map((header) => (
                  (header.name === 'age' || header.name === 'handicap') ? (
                    <th key={`${header.name}`}>
                      <div className='range-column'>
                        <div className='range-field'>
                          <span>{ header.min_label }</span>
                        </div>
                        <div className='range-field'>
                          <span>{ header.max_label }</span>
                        </div>
                      </div>
                    </th>
                  ) : (
                    <th key={`${header.name}`}>{ header.label }</th>
                  )
                ))
              }
            </tr>
          </thead>
          <tbody>
            {
              Object.entries(divisions).map(([ id, division ]) => (
                <>
                  <tr key={id}>
                  {
                    <td>
                      <input
                        className='form-control'
                        type='text'
                        name={`division_name_${id}`}
                        value={division.name || ''}
                        onChange={(e) => updateDivisionRow(id, { name: e.target.value })}/>
                    </td>
                  }
                  {
                    <td>
                      <span>
                        <label>{division.numberOfGolfers} { division.numberOfGolfers === 1 ? golferLabel.one : golferLabel.other }</label>
                      </span>
                    </td>
                  }
                  {
                    withHandicapRange &&
                    <td>
                      <div className='range-column'>
                        <div className='range-field'>
                          <input
                            type='text'
                            name={`min_handicap_${id}}`}
                            className='range-text form-text form-control'
                            placeholder={internationalize('divisions.index.by_multiple_criteria.min')}
                            value={(division.minHandicap === '+100.0' || !division.minHandicap) ? '' : division.minHandicap}
                            onChange={(e) => updateDivisionRow(id, { minHandicap: e.target.value })}
                            onBlur={(e) => fetchAndUpdateDivision(id, { minHandicap: e.target.value })}
                          />
                        </div>
                        <div className='range-field'>
                          <input
                            type='text'
                            name={`max_handicap_${id}}`}
                            className='range-text form-text form-control'
                            placeholder={internationalize('divisions.index.by_multiple_criteria.max')}
                            value={(division.maxHandicap === '100.0' || !division.maxHandicap) ? '' : division.maxHandicap}
                            onChange={(e) => updateDivisionRow(id, { maxHandicap: e.target.value })}
                            onBlur={(e) => fetchAndUpdateDivision(id, { maxHandicap: e.target.value })}
                          />
                        </div>
                      </div>
                    </td>
                  }
                  {
                    withGenders &&
                    <td>
                      {
                        genders.map((gender) =>
                          <label key={`gender_${id}_${gender}`} className={`gender-radio chip ${gender === division.selectedGender ? 'chip-checked' : ''}`} htmlFor={`gender_${id}_${gender}`}>
                            <input
                              id={`gender_${id}_${gender}`}
                              className='no-radio'
                              type='radio'
                              name={`gender_${id}_${gender}`}
                              value={gender}
                              onChange={() => fetchAndUpdateDivision(id, { selectedGender: gender })}
                              checked={division.selectedGender === gender}
                            />
                            <span>{gender}</span>
                          </label>,
                        )
                      }
                    </td>
                  }
                  {
                    withAgeRange &&
                    <td>
                      <div className='range-column'>
                        <div className='range-field'>
                          <input
                            type='number'
                            name={`min_age_${id}}`}
                            min={1}
                            max={100}
                            className='range-text form-text form-control'
                            placeholder={internationalize('divisions.index.by_multiple_criteria.min')}
                            value={(division.minAge === 0 || !division.minAge) ? '' : division.minAge}
                            onChange={(e) => updateDivisionRow(id, { minAge: e.target.value })}
                            onBlur={(e) => fetchAndUpdateDivision(id, { minAge: e.target.value })}
                          />
                        </div>
                        <div className='range-field'>
                          <input
                              type='number'
                              name={`max_age_${id}}`}
                              min={1}
                              max={100}
                              className='range-text form-text form-control'
                              placeholder={internationalize('divisions.index.by_multiple_criteria.max')}
                              value={(division.maxAge === 100 || !division.maxAge) ? '' : division.maxAge}
                              onChange={(e) => updateDivisionRow(id, { maxAge: e.target.value })}
                              onBlur={(e) => fetchAndUpdateDivision(id, { maxAge: e.target.value })}
                            />
                        </div>
                      </div>
                    </td>
                }
                {
                  withCustomField &&
                  <td>
                    <a 
                      onClick={() => openModal(id)}
                      className={!division.metadatumId || !division.metadatumAnswer ? 'btsp_small_white_button action_button' : ''}
                    >
                      {division.metadatumId && division.metadatumAnswer
                        ? `${getCustomFieldLabel(division.metadatumId)} / ${division.metadatumAnswer}`
                        : internationalize('divisions.index.by_multiple_criteria.select_custom_field')}
                    </a>
                  </td>
                }
                {
                  <td>
                    <a className='remove_multiple_criteria_division' href='#' onClick={() => removeRow(id)}>
                      <label>{ internationalize('buttons.delete') }</label>
                    </a>
                  </td>
                }
                </tr>
              </>
            ))}
            {
              <tr className='total_golfers_row'>
                <td>
                  { internationalize('divisions.index.by_multiple_criteria.selected_golfers') }
                </td>
                <td>
                  <span className='members_count'>
                    {
                      golfersLoading ? (
                        <img src={require('ajax-loader.gif')} alt="Loading..."/>
                      ) : (
                        <label>{selectedGolfers}</label>
                      )
                    }
                  </span>
                  <span>
                    {' / ' + leagueMembersCount }
                  </span>
                </td>
                <td colSpan={5}/>
              </tr>
            }
          </tbody>
        </table>
        {
          withCustomField &&
          <SelectCustomField
            showModal={modalOpened}
            onClose={closeModal}
            customFields={customFields}
            customFieldAnswers={customFieldAnswers}
            divisionId={selectedDivisionId}
            metadatumId={divisions[selectedDivisionId]?.metadatumId}
            metadatumAnswer={divisions[selectedDivisionId]?.metadatumAnswer}
            fetchAndUpdateDivision={fetchAndUpdateDivision}
          />
        }
      </div>
      <div className='add_new_division divisions-flights-action-buttons'>
        <a className='add_another_multiple_criteria_division action-button' href='#' onClick={addRow}>
          { internationalize('divisions.index.by_multiple_criteria.add_another_division') }
        </a>
      </div>
    </div>
  )
}

MultipleCriteriaTable.propTypes = {
  criteria: PropTypes.object,
  golferLabel: PropTypes.object,
  teeAndHole: {
    tee: PropTypes.string,
    hole: PropTypes.string,
    virtual_id: PropTypes.string,
  },
  adjustmentHandicap: PropTypes.string,
  genderAdjustmentHandicap: PropTypes.object,
  dateForAge: PropTypes.number,
  useFirstRoundHI: PropTypes.string,
  divisions: PropTypes.object,
  customFields: PropTypes.object,
  customFieldAnswers: PropTypes.object,
  modalOpened: PropTypes.bool,
  closeModal: PropTypes.func,
  setData: PropTypes.func,
  updateDivisionRow: PropTypes.func,
  getMatchingMembers: PropTypes.func,
  leagueMembersCount: PropTypes.number,
}

export default MultipleCriteriaTable
